import { TabModel } from '../../common/types/TabModel';
import { RootStore } from '../../common/stores/context';
import { DocumentVisualStore } from '../../documents/stores';
import { DocumentService, SessionService, ProjectsService, ImageService } from '../../common/services';
import { ContractsIngestionService } from '../services';
import { AppStoreBase, DocumentPreviewStore, SourceStore, FormStore } from '../../custom_shared/stores';
import SessionStore from './SessionStore';
import NodesStore from './NodesStore';
import InputsStore from './InputsStore';
import ActionStore from './ActionStore';
import SettingsStore from './SettingsStore';
import BusinessSearchStore from './BusinessSearchStore';
import ContractSubmissionStore from './ContractSubmissionStore';
import RenewalSuggestionsStore from './RenewalSuggestionsStore';
import ControlStateStore from './ControlStateStore';
import BaseCompanySegregationStore from './BaseCompanySegregationStore';
import ContractComparisonStore from './ContractComparisonStore';

export default class AppStore extends AppStoreBase {
    readonly formStore: FormStore;
    readonly sessionStore: SessionStore;
    readonly sourceStore: SourceStore;
    readonly nodesStore: NodesStore;
    readonly inputsStore: InputsStore;
    readonly actionStore: ActionStore;
    readonly settingsStore: SettingsStore;
    readonly documentPreviewStore: DocumentPreviewStore;
    readonly documentVisualStore: DocumentVisualStore;
    readonly contractSubmissionStore: ContractSubmissionStore;
    readonly businessSearchStore: BusinessSearchStore;
    readonly renewalSuggestionsStore: RenewalSuggestionsStore;
    readonly controlStateStore: ControlStateStore;
    readonly baseCompanySegregationStore: BaseCompanySegregationStore;
    readonly contractComparisonStore: ContractComparisonStore;

    constructor(
        readonly tab: TabModel,
        readonly rootStore: RootStore
    ) {
        super(tab, rootStore);

        const contractsIngestionService = new ContractsIngestionService();

        this.formStore = new FormStore();

        this.sessionStore = new SessionStore(this.tab, this.rootStore.iotaSessionStore);

        this.sourceStore = new SourceStore(this, this.sessionStore, new SessionService());

        this.nodesStore = new NodesStore(this.sessionStore, this.sourceStore);

        this.inputsStore = new InputsStore(this.nodesStore);

        this.settingsStore = new SettingsStore(this.sessionStore);

        this.actionStore = new ActionStore(this, this.sessionStore, this.nodesStore, this.settingsStore);

        this.contractSubmissionStore = new ContractSubmissionStore(
            this.formStore,
            this.nodesStore,
            this.sessionStore,
            this.actionStore,
            contractsIngestionService
        );

        this.baseCompanySegregationStore = new BaseCompanySegregationStore(
            this,
            this.sessionStore,
            this.contractSubmissionStore,
            contractsIngestionService
        );

        this.documentVisualStore = new DocumentVisualStore(
            this.rootStore.applicationDefinitionsStore,
            new ImageService(),
            this.rootStore.errorStore,
            new DocumentService()
        );

        this.documentPreviewStore = new DocumentPreviewStore(
            this,
            this.inputsStore,
            this.documentVisualStore,
            new ProjectsService()
        );

        this.businessSearchStore = new BusinessSearchStore(
            this,
            this.sessionStore,
            this.sourceStore,
            contractsIngestionService
        );

        this.renewalSuggestionsStore = new RenewalSuggestionsStore(
            this,
            this.nodesStore,
            this.sessionStore,
            this.contractSubmissionStore,
            this.settingsStore,
            this.baseCompanySegregationStore,
            contractsIngestionService
        );

        this.controlStateStore = new ControlStateStore(
            this.inputsStore,
            this.sessionStore,
            this.contractSubmissionStore
        );

        this.contractComparisonStore = new ContractComparisonStore(
            this,
            this.sessionStore,
            this.actionStore,
            this.nodesStore,
            this.inputsStore,
            this.sourceStore,
            this.settingsStore,
            contractsIngestionService
        );
    }

    dispose() {
        this.sourceStore.dispose();
        this.nodesStore.dispose();
        this.inputsStore.dispose();
        this.settingsStore.dispose();
        this.actionStore.dispose();
        this.documentPreviewStore.dispose();
        this.contractSubmissionStore.dispose();
        this.renewalSuggestionsStore.dispose();
        this.businessSearchStore.dispose();
        this.contractComparisonStore.dispose();
        this.removeAppTab();
    }
}
